/**
 * Teams domain (agency sub-admin roles + team members) - built on apiClient
 * via useRequest, not useAppServices(). Covers two separate hook/services.js
 * blocks:
 *   - `roles.*` -> Teams/Roles/* (role CRUD)
 *   - `user.*` (singular - the general user CRUD, not a "teams"-specific
 *     namespace) + `auth.*Team` -> Teams/Users/* (team member CRUD)
 *
 * Team members are created/edited through `auth.CreateTeam`/`auth.EditTeam`
 * (POST/PUT /auth/team) but listed/deleted through the generic `user`
 * namespace (GET /user/filter) - that split is pre-existing, not introduced
 * here.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const roleKeys = {
  all: ["roles"],
  list: (agencyId) => ["roles", agencyId],
};

// Teams/Roles/components/data/dataTableData.js, also reused by
// Teams/Users/components/data/dataTableData.js to build the role picker.
export function useRolesList(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: roleKeys.list(agencyId),
    queryFn: async () => {
      const { response } = await request(GET, "/roles", { query: `agency_id=${agencyId}` });
      return response?.data || [];
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useCreateRole() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/roles", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: roleKeys.all }),
  });
}

export function useUpdateRole() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/roles", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: roleKeys.all }),
  });
}

export function useDeleteRole() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (query) => request(DELETE, "/roles", { query }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: roleKeys.all }),
  });
}

export const teamUserKeys = {
  all: ["team-users"],
  list: (agencyId) => ["team-users", agencyId],
};

// Teams/Users/components/data/dataTableData.js - GET /user/filter, same
// endpoint the rest of the app's `user.filter` calls use.
export function useTeamUsersList(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: teamUserKeys.list(agencyId),
    queryFn: async () => {
      const { response } = await request(GET, "/user/filter", { query: `agency_id=${agencyId}` });
      return response?.data || [];
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useCreateTeamUser() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/auth/team", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamUserKeys.all }),
  });
}

export function useUpdateTeamUser() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/auth/team", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamUserKeys.all }),
  });
}

// ponytail: pre-existing bug, preserved rather than fixed. The old call site
// was `AppService.user.delete(...)`, which doesn't exist anywhere in
// hook/services.js's `user` block (only get/filter/register/add_team/
// location_user/update/subscriber/agency_user) - so this delete button has
// always thrown a client-side TypeError and deleted nothing. There's no
// unambiguous real replacement in that same namespace (unlike saas.delete):
// a `users` (plural) CRUD block has a working `delete`, and the backend
// separately exposes DELETE /user/team-member/:id - either could be the
// real intent, but picking one would be guessing. Kept pointed at the `user`
// singular base path so the no-op end result (delete still does nothing) is
// unchanged; flagged in the migration report for a human to pick the real fix.
export function useDeleteTeamUser() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (query) => request(DELETE, "/user", { query }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamUserKeys.all }),
  });
}
